import { AccumulatorBranch } from "./AccumulatorBranch";
import { LinkFunction, LinkOutput } from "./Link";
import { PerceptualFunction, Pr } from "./Pr";
import { BrakePedalControl } from "./BrakePedalControl";

let excitatoryPerceptualFunction: (K: number) => PerceptualFunction = K => (
  x: number
) => {
  return K * x;
};

let inhibitoryPerceptualFunction: (K: number) => PerceptualFunction = K => (
  x: number
) => {
  return K * Math.abs(x);
};

export class DriverModel {
  // List the properties
  excitatoryBranch: AccumulatorBranch;
  inhibitoryBranch: AccumulatorBranch;
  branchLink: LinkFunction;
  control: BrakePedalControl;
  bpTarget: number = 0;
  isReleasing: boolean = false;
  doNotBrake: boolean = false;

  // simulation parameters
  dt: number = 0.01;

  // driver model parameters
  Kex: number;
  kex: number;
  gateex: number;
  Kin: number;
  kin: number;
  gatein: number;
  tau_p: number = 0.05;
  tau_m: number = 0.1;

  // Constructor
  constructor(
    Kex: number,
    kex: number,
    gateex: number,
    Kin: number,
    kin: number,
    gatein: number,
    tau_p?: number,
    tau_m?: number,
    dt?: number
  ) {
    this.Kex = Kex;
    this.kex = kex;
    this.gateex = gateex;
    this.Kin = Kin;
    this.kin = kin;
    this.gatein = gatein;
    if (dt !== undefined) this.dt = dt;
    if (tau_p !== undefined) this.tau_p = tau_p;
    if (tau_m !== undefined) this.tau_m = tau_m;
    this.excitatoryBranch = new AccumulatorBranch(
      excitatoryPerceptualFunction(this.Kex),
      this.kex,
      this.gateex,
      this.dt
    );
    this.inhibitoryBranch = new AccumulatorBranch(
      inhibitoryPerceptualFunction(this.Kin),
      this.kin,
      this.gatein,
      this.dt
    );
    this.branchLink = (activity: number[], Pr: Pr[]) => {
      let out: LinkOutput;
      let reset = new Array(2).fill(false);
      let value: number = undefined;
      // if inhibitory activity is bigger than 1, reset everything and release the brake pedal
      if (activity[1] > 1) {
        reset = [true, true];
        value = 0;
      } else if (activity[0] > 1) {
        reset = [true, false];
        value = Math.min(1, Pr[0].getValue());
      }

      out = { reset: reset, outVal: value };
      return out;
    };
  }

  reinit(driverParams: number[], dt?: number) {
    if (dt !== undefined) this.dt = dt;
    this.bpTarget = 0;
    this.isReleasing = false;
    this.doNotBrake = false;

    this.Kex = driverParams[0];
    this.kex = driverParams[1];
    this.gateex = driverParams[2];
    this.Kin = driverParams[3];
    this.kin = driverParams[4];
    this.gatein = driverParams[5];
    this.excitatoryBranch = new AccumulatorBranch(
      excitatoryPerceptualFunction(this.Kex),
      this.kex,
      this.gateex,
      this.dt
    );
    this.inhibitoryBranch = new AccumulatorBranch(
      inhibitoryPerceptualFunction(this.Kin),
      this.kin,
      this.gatein,
      this.dt
    );
    this.control = new BrakePedalControl(this.dt, this.tau_m);
  }
  // Functions
  update(
    t: number,
    looming: number,
    PPET: number,
    latDist: number[],
    bicycleSpeed: number
  ) {
    // latDist[0]: xDist between front of bicycle and right of car
    // latDist[1]: xDist between rear of bicycle and left of car

    if (!this.doNotBrake) {
      let linkOutput: LinkOutput;

      if (latDist[1] < 0 || (latDist[0] > 0 && bicycleSpeed < 0.2)) {
        this.excitatoryBranch.Pr.reset();
        this.excitatoryBranch.Pp.reset();
        this.excitatoryBranch.reset();
        if (this.isReleasing) {
          // if the brake pedal is already releasing, set all the parameters to 0
          this.inhibitoryBranch.Pr.reset();
          this.inhibitoryBranch.Pp.reset();
          this.inhibitoryBranch.reset();
        }
      }

      this.excitatoryBranch.update(t, looming);
      this.inhibitoryBranch.update(t, PPET);

      linkOutput = this.branchLink(
        [this.excitatoryBranch.getValue(), this.inhibitoryBranch.getValue()],
        [this.excitatoryBranch.Pr, this.inhibitoryBranch.Pr]
      );
      if (linkOutput.reset[0]) {
        this.excitatoryBranch.accumulator.updateEpsilon(
          this.excitatoryBranch.Pr.getValue(),
          this.excitatoryBranch.Pp.getValue()
        );
        this.excitatoryBranch.Pp.prevValue = this.excitatoryBranch.Pp.getValue();
        this.excitatoryBranch.Pp.value = this.excitatoryBranch.Pr.getValue();
        this.excitatoryBranch.tadj = t;
        this.excitatoryBranch.reset();
        this.isReleasing = false;
      }
      if (linkOutput.reset[1]) {
        this.inhibitoryBranch.accumulator.updateEpsilon(
          this.inhibitoryBranch.Pr.getValue(),
          this.inhibitoryBranch.Pp.getValue()
        );
        this.inhibitoryBranch.Pp.prevValue = this.inhibitoryBranch.Pp.getValue();
        this.inhibitoryBranch.Pp.value = this.inhibitoryBranch.Pr.getValue();
        this.inhibitoryBranch.tadj = t;
        this.inhibitoryBranch.reset();
        this.isReleasing = true;
        if (latDist[1] < 0 || (latDist[0] > 0 && bicycleSpeed < 0.2)) {
          this.doNotBrake = true;
          this.inhibitoryBranch.Pp.reset();
          this.inhibitoryBranch.Pr.reset();
        }
      }
      if (linkOutput.outVal != undefined) this.bpTarget = linkOutput.outVal;
    }
    this.control.update(this.bpTarget);
  }

  getBrakeDeflection() {
    return this.control.getValue();
  }
}
